import { useEffect, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from "react";

import { clamp } from "./syncedTimelineMath";
import type {
  PlaybackCapabilities,
  PlaybackTimeRange,
  VideoTimelineSnapshot,
  VideoTimelineState,
} from "./types";

export const DEFAULT_PLAYBACK_CAPABILITIES: PlaybackCapabilities = Object.freeze({
  live: false,
  play: true,
  seek: true,
  playbackRate: true,
});

export function resolvePlaybackCapabilities(
  snapshot: Pick<VideoTimelineSnapshot, "capabilities">,
): PlaybackCapabilities {
  return snapshot.capabilities ?? DEFAULT_PLAYBACK_CAPABILITIES;
}

/** A paused transport may start unless it explicitly publishes `play: false`. */
export function resolvePlaybackCanStart(
  snapshot: Pick<VideoTimelineSnapshot, "capabilities">,
): boolean {
  return resolvePlaybackCapabilities(snapshot).play !== false;
}

function validSeekableRange(
  range: PlaybackTimeRange | undefined,
  durationSeconds: number,
): PlaybackTimeRange | null {
  if (
    !range ||
    !Number.isFinite(range.startSeconds) ||
    !Number.isFinite(range.endSeconds) ||
    range.endSeconds < range.startSeconds
  ) {
    return null;
  }
  const durationEnd = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 0;
  const startSeconds =
    durationEnd > 0 ? clamp(range.startSeconds, 0, durationEnd) : Math.max(0, range.startSeconds);
  const endSeconds =
    durationEnd > 0
      ? clamp(range.endSeconds, startSeconds, durationEnd)
      : Math.max(startSeconds, range.endSeconds);
  return { startSeconds, endSeconds };
}

export function resolvePlaybackSeekRange(
  snapshot: Pick<VideoTimelineSnapshot, "durationSeconds" | "capabilities">,
): PlaybackTimeRange {
  const durationSeconds =
    Number.isFinite(snapshot.durationSeconds) && snapshot.durationSeconds > 0
      ? snapshot.durationSeconds
      : 0;
  return (
    validSeekableRange(resolvePlaybackCapabilities(snapshot).seekableRange, durationSeconds) ?? {
      startSeconds: 0,
      endSeconds: durationSeconds,
    }
  );
}

export function clampPlaybackSeekTime(
  snapshot: Pick<VideoTimelineSnapshot, "durationSeconds" | "capabilities">,
  seconds: number,
): number {
  const fallbackSeconds = Number.isFinite(seconds) ? seconds : 0;
  const durationSeconds =
    Number.isFinite(snapshot.durationSeconds) && snapshot.durationSeconds > 0
      ? snapshot.durationSeconds
      : 0;
  const capabilityRange = validSeekableRange(
    resolvePlaybackCapabilities(snapshot).seekableRange,
    durationSeconds,
  );
  if (capabilityRange) {
    return clamp(fallbackSeconds, capabilityRange.startSeconds, capabilityRange.endSeconds);
  }
  return durationSeconds > 0
    ? clamp(fallbackSeconds, 0, durationSeconds)
    : Math.max(0, fallbackSeconds);
}

/**
 * 모든 재생 소비자(예: `VideoPlaybackBar`)가 바인딩하는 명령(command)/텔레메트리(telemetry)
 * 계약. 옛 `state` + `onStateChange` 왕복(round-trip)을 대체한다.
 *
 * - **명령(command)** (의도 입력): `play` / `pause` / `toggle` / `seekToSeconds` /
 *   `setPlaybackRate` / `reset`. 외부 탐색(seek)은 *오직* 이 경로로만 들어오므로,
 *   엔진이 "내 에코(echo)"인지 "외부 탐색(seek)"인지 가릴 일이 없다 — 옛 정체성(`WeakSet`)
 *   계약을 떠받치던 피드백 루프가 사라졌다.
 * - **텔레메트리(telemetry)** (읽기 전용 출력): `subscribe` + `getSnapshot`. `useVideoTimeline`
 *   셀렉터(selector) 훅으로 소비한다. 스냅샷(snapshot)은 읽기만 할 뿐 virtual clock으로
 *   되돌아가지 않으므로, 억제할 에코(echo)가 없다.
 *
 * `useSyncedVideoTimeline`은 virtual clock 기반 transport를 반환하고,
 * `useControlledTransport`는 controlled `VideoTimelineState`(예: 단일 controlled
 * `VideoSurface`)를 같은 형태로 맞춘다.
 */
export interface PlaybackTransport {
  /** 텔레메트리(telemetry) 변화 구독. 구독 해제 함수를 반환한다. */
  subscribe: (listener: () => void) => () => void;
  /** 현재 텔레메트리(telemetry) 스냅샷(snapshot). 변화가 없으면 같은 식별자를 유지한다. */
  getSnapshot: () => VideoTimelineSnapshot;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seekToSeconds: (seconds: number) => void;
  setPlaybackRate: (rate: number) => void;
  /** Reattach to a producer-paced live frontier. Present only for transports with follow policy. */
  goLive?: () => void;
  reset: () => void;
  /**
   * Frame-rate time for cursors. Present only on transports that own a running clock; readers
   * of a transport without one fall back to the snapshot (`resolvePlaybackFrameClock`).
   */
  frameClock?: PlaybackFrameClock;
}

/**
 * The time a transport's clock stands at right now: anchor-based on every animation frame while
 * playing, the snapshot time otherwise. `subscribe` wakes on every frame the clock advances and on
 * every command-driven jump (seek, pause, rate, reset, end). Display only — seeks, trim handles and
 * segment edits keep going through the snapshot and their own snapping, so the throttled snapshot
 * stays what React state reads while a cursor follows this clock through a ref.
 */
export interface PlaybackFrameClock {
  subscribe: (listener: () => void) => () => void;
  getTimeSeconds: () => number;
}

export interface PlaybackFrameClockStore extends PlaybackFrameClock {
  set: (seconds: number) => void;
}

export function createPlaybackFrameClock(initialSeconds: number): PlaybackFrameClockStore {
  let seconds = initialSeconds;
  const listeners = new Set<() => void>();
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getTimeSeconds: () => seconds,
    set(next) {
      if (Object.is(next, seconds)) return;
      seconds = next;
      for (const listener of listeners) listener();
    },
  };
}

const snapshotClocks = new WeakMap<object, PlaybackFrameClock>();

/** The transport's frame clock, or a snapshot-paced stand-in for a transport without one. */
export function resolvePlaybackFrameClock(
  transport: Pick<PlaybackTransport, "subscribe" | "getSnapshot" | "frameClock">,
): PlaybackFrameClock {
  if (transport.frameClock) return transport.frameClock;
  let clock = snapshotClocks.get(transport);
  if (!clock) {
    clock = {
      subscribe: transport.subscribe,
      getTimeSeconds: () => transport.getSnapshot().currentTimeSeconds,
    };
    snapshotClocks.set(transport, clock);
  }
  return clock;
}

/**
 * Follows the frame clock outside React state: `onFrame` runs once on subscribe and then on every
 * frame or jump, so the caller writes a ref or style instead of re-rendering per frame. Subscriber
 * count does not change how often the clock ticks.
 */
export function useVideoFrameTime(
  transport: Pick<PlaybackTransport, "subscribe" | "getSnapshot" | "frameClock">,
  onFrame: (seconds: number) => void,
): void {
  // The clock's frames call the latest committed callback; a render may not commit.
  const onFrameRef = useRef(onFrame);
  useLayoutEffect(() => {
    onFrameRef.current = onFrame;
  }, [onFrame]);
  const clock = resolvePlaybackFrameClock(transport);
  useEffect(() => {
    const apply = () => onFrameRef.current(clock.getTimeSeconds());
    apply();
    return clock.subscribe(apply);
  }, [clock]);
}

/**
 * 타임라인 엔진이 소유하는 작은 외부 store. 단일 스냅샷(snapshot) 객체를 들고 있으며,
 * 네 필드 중 하나가 실제로 바뀔 때만 식별자가 바뀌어 `useSyncExternalStore` 구독자가
 * 진짜 변화에서만 깨어난다.
 */
export interface TimelineStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => VideoTimelineSnapshot;
  setSnapshot: (patch: Partial<VideoTimelineSnapshot>) => void;
  /** 스냅샷(snapshot)을 바꾸지 않고 구독자만 깨운다(스냅샷이 들고 있지 않은 파생값 —
   *  예: 채널 자체 길이 — 이 바뀐 경우). */
  notify: () => void;
}

function snapshotsEqual(a: VideoTimelineSnapshot, b: VideoTimelineSnapshot): boolean {
  const aCapabilities = resolvePlaybackCapabilities(a);
  const bCapabilities = resolvePlaybackCapabilities(b);
  const aRange = aCapabilities.seekableRange;
  const bRange = bCapabilities.seekableRange;
  return (
    a.playing === b.playing &&
    a.currentTimeSeconds === b.currentTimeSeconds &&
    a.durationSeconds === b.durationSeconds &&
    a.playbackRate === b.playbackRate &&
    a.status === b.status &&
    a.followingLive === b.followingLive &&
    aCapabilities.live === bCapabilities.live &&
    resolvePlaybackCanStart(a) === resolvePlaybackCanStart(b) &&
    aCapabilities.seek === bCapabilities.seek &&
    aCapabilities.playbackRate === bCapabilities.playbackRate &&
    aRange?.startSeconds === bRange?.startSeconds &&
    aRange?.endSeconds === bRange?.endSeconds
  );
}

export function createTimelineStore(initial: VideoTimelineSnapshot): TimelineStore {
  let snapshot = initial;
  const listeners = new Set<() => void>();

  const emit = () => {
    for (const listener of listeners) listener();
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return snapshot;
    },
    setSnapshot(patch) {
      const next = { ...snapshot, ...patch };
      if (snapshotsEqual(next, snapshot)) return;
      snapshot = next;
      emit();
    },
    notify: emit,
  };
}

/**
 * `TimelineStore`를 소유한 소비자가 같은 store를 명령 경로로도 쓰게 하는 `PlaybackTransport`
 * 어댑터. `useControlledTransport`와 달리 시간이 React 상태를 왕복하지 않으므로, 고빈도
 * tick(재생 시간 보고)이 소유 컴포넌트 트리를 리렌더하지 않고 셀렉터 구독자
 * (`useVideoTimeline`)만 깨운다.
 */
export function createTimelineStoreTransport(store: TimelineStore): PlaybackTransport {
  const seekToSeconds = (seconds: number) => {
    const snapshot = store.getSnapshot();
    if (!resolvePlaybackCapabilities(snapshot).seek) return;
    store.setSnapshot({
      currentTimeSeconds: clampPlaybackSeekTime(snapshot, seconds),
      ...(snapshot.followingLive === undefined ? {} : { followingLive: false }),
    });
  };
  return {
    subscribe: store.subscribe,
    getSnapshot: store.getSnapshot,
    play: () => {
      if (resolvePlaybackCanStart(store.getSnapshot())) store.setSnapshot({ playing: true });
    },
    pause: () => store.setSnapshot({ playing: false }),
    toggle: () => {
      const snapshot = store.getSnapshot();
      if (snapshot.playing) {
        store.setSnapshot({ playing: false });
      } else if (resolvePlaybackCanStart(snapshot)) {
        store.setSnapshot({ playing: true });
      }
    },
    seekToSeconds,
    setPlaybackRate: (rate) => {
      if (
        resolvePlaybackCapabilities(store.getSnapshot()).playbackRate &&
        Number.isFinite(rate) &&
        rate > 0
      ) {
        store.setSnapshot({ playbackRate: rate });
      }
    },
    goLive: () => {
      const snapshot = store.getSnapshot();
      if (!resolvePlaybackCapabilities(snapshot).live || snapshot.followingLive === undefined) {
        return;
      }
      store.setSnapshot({
        currentTimeSeconds: resolvePlaybackSeekRange(snapshot).endSeconds,
        followingLive: true,
      });
    },
    reset: () => {
      const snapshot = store.getSnapshot();
      if (!resolvePlaybackCapabilities(snapshot).seek) {
        store.setSnapshot({
          playing: false,
          ...(snapshot.followingLive === undefined ? {} : { followingLive: false }),
        });
        return;
      }
      store.setSnapshot({
        playing: false,
        currentTimeSeconds: resolvePlaybackSeekRange(snapshot).startSeconds,
        ...(snapshot.followingLive === undefined ? {} : { followingLive: false }),
      });
    },
  };
}

/**
 * transport 텔레메트리(telemetry)의 한 조각을 구독한다. 선택한 값이 `isEqual`(기본
 * `Object.is`) 기준으로 바뀔 때만 리렌더한다 — 그래서 `playing`을 선택한 버튼 행은 매
 * 재생 프레임마다 재조정되지 않고, `currentTimeSeconds`를 선택한 슬라이더만 재조정된다.
 * 선택값을 캐시해 변화 없는 프레임은 같은 참조를 반환한다(`useSyncExternalStore` 무한
 * 루프·불필요한 리렌더 방지).
 */
export function useVideoTimeline<T>(
  transport: Pick<PlaybackTransport, "subscribe" | "getSnapshot">,
  selector: (snapshot: VideoTimelineSnapshot) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): T {
  const cacheRef = useRef<{ value: T } | null>(null);

  const getSelection = () => {
    const next = selector(transport.getSnapshot());
    const cache = cacheRef.current;
    if (cache && isEqual(cache.value, next)) return cache.value;
    cacheRef.current = { value: next };
    return next;
  };

  return useSyncExternalStore(transport.subscribe, getSelection, getSelection);
}

/**
 * controlled `VideoTimelineState`(단일 `VideoSurface` 단독 사용 케이스)를
 * `PlaybackTransport` 형태로 맞춰, 동일한 `VideoPlaybackBar`가 구동하게 한다.
 *
 * 멀티채널 엔진과 달리 이 케이스는 #1 에코(echo) 취약성의 원인이 아니다: 단일
 * `VideoSurface`는 자기 에코(echo)를 숫자(tolerance) 기준으로 억제(clone에 안전)하므로, 여기선
 * 평범한 `state` 왕복도 괜찮다. 이 어댑터는 단지 bar에 동일한 입력 형태 하나를 줄 뿐이다.
 */
export function useControlledTransport(
  state: VideoTimelineState,
  onStateChange: (next: VideoTimelineState) => void,
): PlaybackTransport {
  const stateRef = useRef(state);
  stateRef.current = state;
  const onChangeRef = useRef(onStateChange);
  onChangeRef.current = onStateChange;

  const listenersRef = useRef<Set<() => void> | null>(null);
  if (listenersRef.current === null) listenersRef.current = new Set();

  // controlled state 객체가 바뀔 때마다 구독자를 깨운다. (bar는 부모를 통해서도 리렌더되므로,
  // 이건 부모 렌더 경로 밖의 구독자를 위한 이중 안전장치다.)
  useEffect(() => {
    const listeners = listenersRef.current;
    if (listeners) for (const listener of listeners) listener();
  }, [state]);

  return useMemo<PlaybackTransport>(() => {
    const listeners = listenersRef.current!;
    const emit = (patch: Partial<VideoTimelineState>) =>
      onChangeRef.current({ ...stateRef.current, ...patch });
    const seek = (seconds: number) => {
      const snapshot = stateRef.current;
      if (!resolvePlaybackCapabilities(snapshot).seek) return;
      emit({
        currentTimeSeconds: clampPlaybackSeekTime(snapshot, seconds),
        ...(snapshot.followingLive === undefined ? {} : { followingLive: false }),
      });
    };

    return {
      subscribe(listener) {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
      getSnapshot() {
        return stateRef.current;
      },
      play() {
        if (resolvePlaybackCanStart(stateRef.current)) emit({ playing: true });
      },
      pause() {
        emit({ playing: false });
      },
      toggle() {
        const snapshot = stateRef.current;
        if (snapshot.playing) {
          emit({ playing: false });
        } else if (resolvePlaybackCanStart(snapshot)) {
          emit({ playing: true });
        }
      },
      seekToSeconds: seek,
      setPlaybackRate(rate) {
        if (
          resolvePlaybackCapabilities(stateRef.current).playbackRate &&
          Number.isFinite(rate) &&
          rate > 0
        ) {
          emit({ playbackRate: rate });
        }
      },
      goLive() {
        const snapshot = stateRef.current;
        if (!resolvePlaybackCapabilities(snapshot).live || snapshot.followingLive === undefined) {
          return;
        }
        emit({
          currentTimeSeconds: resolvePlaybackSeekRange(snapshot).endSeconds,
          followingLive: true,
        });
      },
      reset() {
        const snapshot = stateRef.current;
        emit(
          resolvePlaybackCapabilities(snapshot).seek
            ? {
                playing: false,
                currentTimeSeconds: resolvePlaybackSeekRange(snapshot).startSeconds,
                ...(snapshot.followingLive === undefined ? {} : { followingLive: false }),
              }
            : {
                playing: false,
                ...(snapshot.followingLive === undefined ? {} : { followingLive: false }),
              },
        );
      },
    };
  }, []);
}
